import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Cell, Pie, PieChart } from 'recharts';

import { Card, CardContent, CardHeader } from '@src/design-system/card';
import { Text } from '@src/design-system/text';
import { ChartContainer, ChartTooltip } from '@src/design-system/chart';
import { ChartTooltipContent } from '@src/features/dashboard/components/chart-tooltip-content';
import SkeletonLoader from '@src/shared/components/reusables/skeleton';
import { REMINDER_CHANNELS } from '@src/shared/types/reminder';
import reminderApi, {
  type TReminderSummaryChannelRow,
  type TReminderSummaryStats,
} from '@src/features/reminders/services/reminder-api';
import { DefaultDateVlue } from '@src/features/dashboard/constants';
import { getDateRangeObject } from '@src/features/dashboard/helper/date-helper';
import { Message, Sms, Whatsapp } from 'iconsax-react';

type MetricKey = keyof Pick<
  TReminderSummaryStats,
  'sent' | 'delivered' | 'read' | 'bounced' | 'failed'
>;

type ChannelMetric = {
  label: string;
  fill: string;
  key: MetricKey;
};

type ChannelCardConfig = {
  channel: string;
  title: string;
  metrics: ChannelMetric[];
  icon: 'whatsapp' | 'email' | 'sms';
};

const CHANNEL_CARDS: ChannelCardConfig[] = [
  {
    channel: REMINDER_CHANNELS.whatsapp,
    title: 'Whatsapp',
    icon: 'whatsapp',
    metrics: [
      { label: 'Sent', key: 'sent', fill: '#3F83F8' },
      { label: 'Delivered', key: 'delivered', fill: '#5B31AA' },
      { label: 'Read', key: 'read', fill: '#0E9F6E' },
      { label: 'Failed', key: 'failed', fill: '#F05252' },
    ],
  },
  {
    channel: REMINDER_CHANNELS.email,
    title: 'Email',
    icon: 'email',
    metrics: [
      { label: 'Sent', key: 'sent', fill: '#3F83F8' },
      { label: 'Delivered', key: 'delivered', fill: '#6D28D9' },
      { label: 'Opened', key: 'read', fill: '#0E9F6E' },
      { label: 'Bounced', key: 'bounced', fill: '#584777' },
      { label: 'Failed', key: 'failed', fill: '#F05252' },
    ],
  },
  {
    channel: REMINDER_CHANNELS.sms,
    title: 'SMS',
    icon: 'sms',
    metrics: [
      { label: 'Sent', key: 'sent', fill: '#3B82F6' },
      { label: 'Delivered', key: 'delivered', fill: '#6D28D9' },
      { label: 'Failed', key: 'failed', fill: '#EF4444' },
    ],
  },
];

const METRIC_ROW_MIN_HEIGHT = 'min-h-7';
const MAX_METRIC_ROWS = Math.max(
  ...CHANNEL_CARDS.map((card) => card.metrics.length)
);

function normChannel(c: string) {
  return c.trim().toUpperCase();
}

function findChannelRow(
  rows: TReminderSummaryChannelRow[] | undefined,
  channel: string
): TReminderSummaryChannelRow | undefined {
  if (!rows?.length) return undefined;
  const want = normChannel(channel);
  return rows.find((r) => normChannel(r.channel) === want);
}

function metricValue(
  row: TReminderSummaryChannelRow | undefined,
  key: ChannelMetric['key']
): number {
  if (!row?.stats) return 0;
  return row.stats[key] ?? 0;
}

function ChannelIcon({ kind }: { kind: ChannelCardConfig['icon'] }) {
  if (kind === 'whatsapp') {
    return (
      <span className='inline-flex size-5 items-center justify-center rounded-lg text-gray-900'>
        <Whatsapp variant='Bold' />
      </span>
    );
  }
  if (kind === 'email') {
    return (
      <span className='inline-flex size-5 items-center justify-center rounded-lg text-gray-900'>
        <Sms variant='Bold' />
      </span>
    );
  }
  return (
    <span className='inline-flex size-5 items-center justify-center rounded-lg text-gray-900'>
      <Message variant='Bold' />
    </span>
  );
}

function SemiDonut({
  data,
  total,
}: {
  data: { name: string; value: number; fill: string }[];
  total: number;
}) {
  const slices = data.filter((d) => d.value > 0);

  if (total <= 0 || slices.length === 0) {
    return (
      <div className='relative mx-auto flex h-[145px] w-full max-w-[280px] items-center justify-center pb-2'>
        <p className=' text-gray-500'>No messages</p>
      </div>
    );
  }

  return (
    <div className='relative mx-auto h-[145px] w-full max-w-[280px]'>
      <ChartContainer
        style={{ height: '110px', width: '100%' }}
        config={{}}
      >
        <PieChart margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
          <ChartTooltip
            cursor={false}
            content={<ChartTooltipContent />}
          />
          <Pie
            data={slices}
            dataKey='value'
            nameKey='name'
            cx='50%'
            cy='100%'
            startAngle={180}
            endAngle={0}
            innerRadius={72}
            outerRadius={100}
            paddingAngle={1}
            cornerRadius={4}
            stroke='#ffffff'
            strokeWidth={2}
          >
            {slices.map((entry) => (
              <Cell
                key={entry.name}
                fill={entry.fill}
              />
            ))}
          </Pie>
        </PieChart>
      </ChartContainer>
      <div className='pointer-events-none absolute inset-x-0 bottom-8 flex flex-col items-center text-center'>
        <p className='text-base font-semibold tabular-nums text-gray-800'>
          {total.toLocaleString()}
        </p>
        <Text className='text-[11px] font-medium uppercase tracking-wide text-gray-600'>
          Total messages
        </Text>
      </div>
    </div>
  );
}

function ChannelSummaryCard({
  config,
  row,
}: {
  config: ChannelCardConfig;
  row?: TReminderSummaryChannelRow;
}) {
  const beTotal = row?.stats?.total ?? 0;

  const pieSlices = config.metrics.map((m) => ({
    name: m.label,
    value: metricValue(row, m.key),
    fill: m.fill,
  }));

  const emptyMetricRows = MAX_METRIC_ROWS - config.metrics.length;

  return (
    <Card className='flex h-full min-h-0 w-full flex-col rounded-2xl border border-primary-100 bg-white shadow-sm'>
      <CardHeader className='px-5 pb-2 pt-5'>
        <div className='flex items-center gap-1'>
          <ChannelIcon kind={config.icon} />
          <p className='text-[22px] font-semibold text-gray-900'>
            {config.title}
          </p>
        </div>
      </CardHeader>
      <CardContent className='flex flex-1 flex-col gap-4 px-5 pb-5 pt-0'>
        <SemiDonut
          data={pieSlices}
          total={beTotal}
        />
        <div className='mt-auto flex flex-col gap-2.5 border-t border-primary-100 pt-3'>
          {config.metrics.map((m) => {
            const value = metricValue(row, m.key);
            const pct = beTotal > 0 ? Math.round((value / beTotal) * 100) : 0;
            return (
              <div
                key={m.label}
                className={`flex ${METRIC_ROW_MIN_HEIGHT} items-center justify-between gap-2 text-sm`}
              >
                <div className='flex min-w-0 items-center gap-2'>
                  <span
                    className='h-[13px] w-[27px] shrink-0 rounded-[20px]'
                    style={{ backgroundColor: m.fill }}
                  />
                  <span className='truncate font-medium text-gray-1000'>
                    {m.label}
                  </span>
                </div>
                <span className='shrink-0 tabular-nums font-semibold text-gray-1000'>
                  {value.toLocaleString()}{' '}
                  <span className='font-medium text-gray-600'>({pct}%)</span>
                </span>
              </div>
            );
          })}
          {Array.from({ length: emptyMetricRows }).map((_, index) => (
            <div
              key={`metric-spacer-${index}`}
              className={METRIC_ROW_MIN_HEIGHT}
              aria-hidden
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

export function ReminderOverviewChannelCards() {
  const [params] = useSearchParams();
  const selectedChannels = useMemo(
    () => params.getAll('channel'),
    [params.toString()]
  );
  const selectedPrograms = useMemo(
    () => params.getAll('programs[]'),
    [params.toString()]
  );
  const dateFilter = params.get('date') || DefaultDateVlue;

  const queryParams = useMemo(() => {
    const range = getDateRangeObject(dateFilter);
    const out: Record<string, string | string[]> = {};
    if (range) {
      out.start_date = range.start_date;
      out.end_date = range.end_date;
    }
    if (selectedChannels.length > 0) out.channel = selectedChannels;
    if (selectedPrograms.length > 0) out.programs = selectedPrograms;
    return out;
  }, [dateFilter, selectedChannels, selectedPrograms]);

  const { data: summaryData, isLoading } =
    reminderApi.useGetReminderSummary(queryParams);

  if (isLoading) {
    return (
      <div className='grid w-full grid-cols-1 gap-6 md:grid-cols-3'>
        {[0, 1, 2].map((i) => (
          <Card
            key={i}
            className='flex h-[420px] flex-col rounded-2xl border border-primary-100 bg-white p-4'
          >
            <SkeletonLoader height={360} />
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className='grid w-full grid-cols-1 items-stretch gap-4 md:grid-cols-3'>
      {CHANNEL_CARDS.map((cfg) => (
        <ChannelSummaryCard
          key={cfg.channel}
          config={cfg}
          row={findChannelRow(summaryData, cfg.channel)}
        />
      ))}
    </div>
  );
}
